import React, { useState, useEffect, useContext, useRef } from 'react';
import UserContext from '../context/userContext';
import EnhancedTenantSwitchModal from './EnhancedTenantSwitchModal';
import Cookies from 'js-cookie';
import { getBaseDomain } from '../../utils/domain';

interface CompanySwitchData {
  user_id: number;
  old_company_id: number;
  new_company_id: number;
  old_company_name: string;
  new_company_name: string;
  message: string;
  user_name: string;
  timestamp: string;
}

const BroadcastWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [switchData, setSwitchData] = useState<CompanySwitchData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const { userDetails } = useContext(UserContext) || {};
  const companyNameRef = useRef<string>(userDetails?.company_name || '');
  const tabIdRef = useRef<string>(`tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const lastFetchTimeRef = useRef<number>(0);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const baseDomain = getBaseDomain();

  useEffect(() => {
    companyNameRef.current = userDetails?.company_name || '';
  }, [userDetails?.company_name]);

  const debouncedFetch = (delay: number = 300) => {
    const now = Date.now();
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    if (now - lastFetchTimeRef.current < 1000) {
      return;
    }
    fetchTimeoutRef.current = setTimeout(() => {
      fetchBroadcastMessages();
    }, delay);
  };

  const getAuthToken = (): string | null => {
    return (
      Cookies.get('accessToken') ||
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('auth_token')
    );
  };

  const getCompanySessionId = (): string => {
    let sessionId = Cookies.get('company_session_id');
    if (sessionId) return sessionId;
    sessionId = localStorage.getItem('company_session_id') || '';
    if (sessionId) return sessionId;
    sessionId = sessionStorage.getItem('company_session_id') || '';
    if (sessionId) return sessionId;
    if (typeof document !== 'undefined') {
      const name = 'company_session_id=';
      const decodedCookie = decodeURIComponent(document.cookie);
      const cookieArray = decodedCookie.split(';');
      for (let i = 0; i < cookieArray.length; i++) {
        let cookie = cookieArray[i];
        while (cookie.charAt(0) === ' ') {
          cookie = cookie.substring(1);
        }
        if (cookie.indexOf(name) === 0) {
          sessionId = cookie.substring(name.length, cookie.length);
          return sessionId;
        }
      }
    }
    return '';
  };

  const fetchBroadcastMessages = async () => {
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 1000) {
      return;
    }
    lastFetchTimeRef.current = now;
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    try {
      const authHeader = getAuthToken();
      if (!authHeader) return;
      const companySessionId = getCompanySessionId();
      if (!companySessionId) return;
      if (!userDetails?.company_name) return;
      const params = new URLSearchParams({
        tab_id: tabIdRef.current,
        session_id: companySessionId
      });
      if (lastMessageId) {
        params.append('after_id', lastMessageId);
      }
      const apiUrl = `https://globalapi.${baseDomain}/api/v1/broadcast/?${params}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authHeader}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.status === 401) {
        const globalUrl = `https://app.${baseDomain}/`;
        window.location.href = globalUrl;
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.messages && data.messages.length > 0) {
        const sortedMessages = data.messages.sort((a: any, b: any) => {
          const timestampA = new Date(a.timestamp).getTime();
          const timestampB = new Date(b.timestamp).getTime();
          return timestampB - timestampA;
        });
        const latestMessage = sortedMessages[0];
        setLastMessageId(latestMessage.id);
        processMessage(latestMessage);
      }
    } catch (error) {
      console.error(`[${requestId}] Error fetching broadcast messages:`, error);
    }
  };

  const processMessage = (message: any) => {
    switch (message.type) {
      case 'company_switched':
        handleCompanySwitch(message.data);
        break;
      case 'notification':
        break;
      default:
        break;
    }
  };

  const handleCompanySwitch = (data: any) => {
    const companySwitchData: CompanySwitchData = {
      user_id: data.user_id,
      old_company_id: data.old_company_id,
      new_company_id: data.new_company_id,
      old_company_name: data.old_company_name,
      new_company_name: data.new_company_name,
      message: data.message,
      user_name: data.user_name,
      timestamp: data.timestamp || new Date().toISOString()
    };
    const currentCompanyName = companyNameRef.current;
    if (currentCompanyName !== companySwitchData.new_company_name) {
      setSwitchData(companySwitchData);
      setIsModalVisible(true);
      companyNameRef.current = companySwitchData.new_company_name;
    }
  };

  useEffect(() => {
    const initialFetchTimeout = setTimeout(() => {
      fetchBroadcastMessages();
      try {
        const tabKey = `broadcast_tab_${tabIdRef.current}`;
        sessionStorage.setItem(tabKey, 'active');
        sessionStorage.setItem('broadcast_active_tab', tabIdRef.current);
      } catch (e) {
        console.error('Could not register tab:', e);
      }
    }, 500);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        try {
          const tabKey = `broadcast_tab_${tabIdRef.current}`;
          sessionStorage.setItem(tabKey, 'active');
          sessionStorage.setItem('broadcast_active_tab', tabIdRef.current);
          sessionStorage.setItem('broadcast_last_active', Date.now().toString());
        } catch (e) {
          console.error('Could not update tab status:', e);
        }
        debouncedFetch(200);
      } else {
        try {
          const tabKey = `broadcast_tab_${tabIdRef.current}`;
          sessionStorage.setItem(tabKey, 'inactive');
          sessionStorage.setItem('broadcast_last_inactive', Date.now().toString());
        } catch (e) {
          console.error('Could not update tab status:', e);
        }
      }
    };

    const handlePageShow = () => {
      fetchBroadcastMessages();
    };
    const handlePageHide = () => {
      // just record if needed
    };
    document.addEventListener('visibilitychange', handleVisibilityChange, { passive: true });
    window.addEventListener('pageshow', handlePageShow, { passive: true });
    window.addEventListener('pagehide', handlePageHide, { passive: true });
    return () => {
      clearTimeout(initialFetchTimeout);
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
      try {
        const tabKey = `broadcast_tab_${tabIdRef.current}`;
        sessionStorage.removeItem(tabKey);
        if (sessionStorage.getItem('broadcast_active_tab') === tabIdRef.current) {
          sessionStorage.removeItem('broadcast_active_tab');
        }
      } catch (e) {
        console.error('Could not clean up tab registration:', e);
      }
    };
  }, [userDetails?.company_name]);

  const refreshBroadcastMessages = () => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    fetchBroadcastMessages();
    try {
      const timestamp = Date.now();
      localStorage.setItem('broadcast_last_fetch', timestamp.toString());
      localStorage.setItem('manual_refresh_at', timestamp.toString());
      const tabKey = `broadcast_tab_${tabIdRef.current}`;
      sessionStorage.setItem(tabKey, 'active');
      sessionStorage.setItem('broadcast_active_tab', tabIdRef.current);
    } catch (e) {
      console.error('Could not update manual refresh timestamp:', e);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshBroadcastMessages = refreshBroadcastMessages;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).refreshBroadcastMessages;
      }
    };
  }, []);

  return (
    <>
      {children}
      {isModalVisible && switchData && (
        <EnhancedTenantSwitchModal
          isVisible={isModalVisible}
          switchData={switchData}
          tabId={tabIdRef.current}
        />
      )}
    </>
  );
};

export default BroadcastWrapper;

